import { palette } from "lib/miolo";
import BankFundamentalsToggles from "components/company/financials/american/fundamentals/BankFundamentalsToggles";
import CompanyFundamentalsToggles from "components/company/financials/american/fundamentals/CompanyFundamentalsToggles";
import InsurerFundamentalsToggles from "components/company/financials/american/fundamentals/InsurerFundamentalsToggles";
import ReitFundamentalsToggles from "components/company/financials/american/fundamentals/ReitFundamentalsToggles";
import formatFinancialValueForChart from "components/company/financials/formatFinancialValueForChart";
import type { TCompanyFinancialsClassification } from "components/fundamentals/classifyFinancialStatements";
import type { FundamentalsLine } from "components/fundamentals/FundamentalsChart";
import FundamentalsChart from "components/fundamentals/FundamentalsChart";
import FundamentalsChartBlankslate from "components/fundamentals/FundamentalsChartBlankslate";
import FundamentalsChartBoxHeader from "components/fundamentals/FundamentalsChartBoxHeader";
import FundamentalsChartContext from "components/fundamentals/FundamentalsChartContext";
import FundamentalsChartContextProvider from "components/fundamentals/FundamentalsChartContextProvider";
import { Fundaments } from "components/fundamentals/Fundaments";
import { parse } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import type {
  IAmericanCompanyIssuerFinancialsFieldsFragment,
  IAmericanCompanyIssuerFinancialsRowFieldsFragment,
  IAmericanCompanyIssuerTopLevelFragment,
} from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import useSelfie from "lib/hooks/useSelfie";
import type { ISelfieQuery } from "lib/queries/__generated__/Selfie.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import type { ReactElement } from "react";
import classifyFinancialStatementsForChart from "components/company/financials/american/fundamentals/classifyFinancialStatementsForChart";

type TFinancialStatementRow = IAmericanCompanyIssuerFinancialsRowFieldsFragment;

interface IFundamentalsChartProps {
  company: IAmericanCompanyIssuerTopLevelFragment;
  financials:
    | IAmericanCompanyIssuerFinancialsFieldsFragment["yearly"]
    | IAmericanCompanyIssuerFinancialsFieldsFragment["quarterly"];
}

export default function AmericanCompanyFundamentalsChart({ company, financials }: IFundamentalsChartProps) {
  const selfie = useSelfie();
  const classification = classifyFinancialStatementsForChart(company, financials);

  if (!selfie) {
    return null;
  }

  return (
    <FundamentalsChartContextProvider fundaments={getDefaultSelectedFundaments(classification, financials, selfie)}>
      <div
        css={{
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          gridColumnGap: 30,
          alignItems: "center",
          margin: "0px auto",
          width: 1140,
        }}
      >
        <div
          css={{
            gridColumn: "span 12",
            boxShadow: "0 4px 6px 0 #33333333",
            backgroundColor: "#ffffff",
            borderRadius: 4,
          }}
        >
          <FundamentalsChartContext.Consumer>
            {({ fundaments }) => (
              <FundamentalsChartBoxHeader title="Fundamentals" grayedOut={fundaments.length === 0} />
            )}
          </FundamentalsChartContext.Consumer>

          <div
            css={{
              padding: "24px 0px",
            }}
          >
            <div
              css={{
                display: "grid",
                gridRowGap: 16,
                padding: "0px 24px 0px 24px",
              }}
            >
              <div
                css={{
                  display: "grid",
                  gridColumnGap: 8,
                  gridAutoFlow: "column",
                  gridAutoColumns: "max-content",
                }}
              >
                <span
                  css={{
                    fontWeight: 500,
                    color: palette.baliHai,
                    fontSize: 14,
                    lineHeight: "18px",
                    letterSpacing: 0.6,
                  }}
                >
                  Indicators
                </span>

                <FundamentalsChartContext.Consumer>
                  {({ selectAll, isAllSelected }) => {
                    return isAllSelected ? null : (
                      <button
                        type="button"
                        onClick={selectAll}
                        css={{
                          border: "none",
                          cursor: "pointer",
                          outline: 0,
                          padding: 0,
                          background: "transparent",
                          fontWeight: 600,
                          color: palette.royalBlue,
                          fontSize: 14,
                          lineHeight: "18px",
                          letterSpacing: 0.6,
                        }}
                      >
                        Show all
                      </button>
                    );
                  }}
                </FundamentalsChartContext.Consumer>

                <FundamentalsChartContext.Consumer>
                  {({ deselectAll, fundaments }) => {
                    return fundaments.length === 0 ? null : (
                      <button
                        type="button"
                        onClick={deselectAll}
                        css={{
                          border: "none",
                          cursor: "pointer",
                          outline: 0,
                          padding: 0,
                          background: "transparent",
                          fontWeight: 600,
                          color: palette.link,
                          fontSize: 14,
                          lineHeight: "18px",
                          letterSpacing: 0.6,
                        }}
                      >
                        Hide all
                      </button>
                    );
                  }}
                </FundamentalsChartContext.Consumer>
              </div>

              <div
                css={{
                  display: "flex",
                  flexWrap: "wrap",

                  "> button": {
                    marginRight: 8,
                    marginBottom: 8,
                  },
                }}
              >
                {classification.isBank && <BankFundamentalsToggles />}
                {classification.isCompany && <CompanyFundamentalsToggles />}
                {classification.isInsurer && <InsurerFundamentalsToggles />}
                {classification.isReit && <ReitFundamentalsToggles />}
              </div>
            </div>

            <FundamentalsChartContext.Consumer>
              {({ fundaments }): ReactElement | null => {
                if (fundaments.length === 0) {
                  return (
                    <div className="flex flex-col items-center space-y-4 text-center">
                      <div className="flex rounded-full bg-gray-50 p-12">
                        <FundamentalsChartBlankslate width={120} height={120} />
                      </div>

                      <p className="font-aeonik font-normal text-gray-600">
                        Choose at least one indicator for viewing it in a chart.
                      </p>
                    </div>
                  );
                }

                return (
                  <FundamentalsChart<TFinancialStatementRow[]>
                    chart={{
                      width: 1140,
                      height: 480,
                      margin: {
                        top: 24,
                        right: fundaments.length === 2 ? 56 : 32,
                        bottom: 24,
                        left: 56,
                      },
                      padding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                      },
                    }}
                    lines={getChartLines(fundaments)}
                    getFiscalDate={({ reportDate }) => {
                      return parse(reportDate, "yyyy-MM-dd", new Date(), {
                        locale: ptBR,
                      });
                    }}
                    yAxes={fundaments.length === 2 ? "left-and-right" : "left-only"}
                    data={financials}
                    axisBottomDateFormat={classification.isQuarterly ? "MM/yy" : "yyyy"}
                  />
                );
              }}
            </FundamentalsChartContext.Consumer>
          </div>
        </div>
      </div>
    </FundamentalsChartContextProvider>
  );
}

function getChartLines(fundaments: Fundaments[]): FundamentalsLine<TFinancialStatementRow>[] {
  const lines: FundamentalsLine<TFinancialStatementRow>[] = [
    {
      key: Fundaments.TOTAL_ASSETS,
      stroke: palette.seaGreen,
      label: "Total Assets",
      value: (datum) => datum.balanceSheet.totalAssets,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.TOTAL_EQUITY,
      stroke: palette.kumera,
      label: "Equity",
      value: (datum) => datum.balanceSheet.totalEquity,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.REVENUE,
      stroke: palette.fruitSalad,
      label: "Revenue",
      value: (datum) => datum.incomeStatement.revenue,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.EBITDA,
      stroke: palette.seaGreen,
      label: "EBITDA",
      value: (datum) => datum.incomeStatement.ebitda,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.NET_INCOME,
      stroke: palette.oceanGreen,
      label: "Net Income",
      value: (datum) => datum.incomeStatement.netIncome,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.NET_MARGIN,
      stroke: "#507712",
      label: "Net Margin",
      value: (datum) => {
        if (datum.incomeStatement.netIncome < 0) {
          return 0;
        }
        return Math.max(datum.incomeStatement.netMargin, 0);
      },
      valueFormatter: (netMargin, datum) => {
        if (datum.incomeStatement.netMargin < 0 || datum.incomeStatement.netIncome < 0) {
          return "L";
        }

        const formattedNetMargin = netMargin.toFixed(netMargin < 1 && netMargin !== 0 ? 1 : 0).replace(".", ",");
        return `${formattedNetMargin}%`;
      },
    },
    {
      key: Fundaments.RETURN_ON_EQUITY,
      stroke: palette.stromboli,
      label: "ROE",
      value: (datum) => {
        if (
          datum.balanceSheet.returnOnEquity < 0 ||
          datum.incomeStatement.netIncome < 0 ||
          datum.balanceSheet.totalEquity < 0
        ) {
          return 0;
        }

        return datum.balanceSheet.returnOnEquity;
      },
      valueFormatter: (returnOnEquity, datum) => {
        if (
          datum.balanceSheet.returnOnEquity < 0 ||
          datum.incomeStatement.netIncome < 0 ||
          datum.balanceSheet.totalEquity < 0
        ) {
          return "L";
        }

        const formattedReturnOnEquity = returnOnEquity.toFixed(returnOnEquity < 1 ? 1 : 0).replace(".", ",");
        return `${formattedReturnOnEquity}%`;
      },
    },
    {
      key: Fundaments.PROVISION_FOR_CREDIT_LOSSES,
      stroke: palette.mojo,
      label: "PCL",
      value: (datum) => datum.incomeStatement.creditLossesProvision,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.CASH,
      stroke: palette.dixie,
      label: "Cash",
      value: (datum) => datum.balanceSheet.cash,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.DEBT,
      stroke: palette.burntUmber,
      label: "Debt",
      value: (datum) => datum.balanceSheet.debt,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.NET_DEBT,
      stroke: palette.mojo,
      label: "Net Debt",
      value: (datum) => datum.balanceSheet.netDebt,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.FUNDS_FROM_OPERATIONS,
      stroke: palette.tundora,
      label: "FFO",
      value: (datum) => datum.cashFlow.fundsFromOperations,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.CASH_FLOW_FROM_OPERATIONS,
      stroke: palette.tundora,
      label: "OCF",
      value: (datum) => datum.cashFlow.cashFlowFromOperations,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.FREE_CASH_FLOW,
      stroke: palette.scienceBlue,
      label: "Free",
      value: (datum) => datum.cashFlow.freeCashFlow,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.CASH_FLOW_FOR_DIVIDENDS,
      stroke: palette.luxorGold,
      label: "Dividends",
      value: (datum) => datum.cashFlow.cashFlowForDividends,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.PAYOUT,
      stroke: "#CB6E17",
      label: "Payout",
      value: (datum) => Math.round(datum.ratios.dividendPayoutRatio * 100),
      valueFormatter: (payout) => `${payout}%`,
    },
    {
      key: Fundaments.DIVIDEND_YIELD,
      stroke: "#C99A2E",
      label: "DY",
      value: (datum) => datum.ratios.dividendYield,
      valueFormatter: (dividendYield) => {
        const formattedDividendYield = dividendYield.toFixed(dividendYield !== 0 ? 1 : 0).replace(".", ",");
        return `${formattedDividendYield}%`;
      },
    },
    {
      key: Fundaments.REPURCHASE_OF_CAPITAL_STOCK,
      stroke: "#C99A2E",
      label: "Buyback",
      value: (datum) => datum.cashFlow.repurchaseOfStock,
      valueFormatter: formatFinancialValueForChart,
    },
    {
      key: Fundaments.EARNINGS_PER_SHARE,
      stroke: palette.mineShaft,
      label: "EPS",
      value: (datum) => (datum.incomeStatement.epsDiluted < 0 ? 0 : datum.incomeStatement.epsDiluted),
      valueFormatter: (earningsPerShareDiluted, datum) => {
        const formatted = earningsPerShareDiluted.toFixed(earningsPerShareDiluted !== 0 ? 2 : 0).replace(".", ",");
        return datum.incomeStatement.epsDiluted < 0 ? "L" : formatted;
      },
    },
    {
      key: Fundaments.FFO_PER_SHARE,
      stroke: palette.mineShaft,
      label: "FPS",
      value: (datum) => (datum.incomeStatement.ffoPerShare < 0 ? 0 : datum.incomeStatement.ffoPerShare),
      valueFormatter: (ffoPerShare) => {
        const formatted = ffoPerShare.toFixed(ffoPerShare !== 0 ? 2 : 0).replace(".", ",");
        return ffoPerShare < 0 ? "L" : formatted;
      },
    },
  ];

  return lines.filter((line) => {
    if (line.key) {
      return fundaments.includes(line.key as Fundaments);
    }

    return false;
  });
}

function getDefaultSelectedFundaments(
  classification: TCompanyFinancialsClassification,
  financials: TFinancialStatementRow[],
  selfie: NonNullable<ISelfieQuery["selfie"]>,
): Fundaments[] {
  if (classification.isBank) {
    return [
      Fundaments.TOTAL_ASSETS,
      Fundaments.TOTAL_EQUITY,
      Fundaments.REVENUE,
      Fundaments.NET_INCOME,
      Fundaments.PROVISION_FOR_CREDIT_LOSSES,
      Fundaments.CASH_FLOW_FOR_DIVIDENDS,
      Fundaments.NET_MARGIN,
      Fundaments.RETURN_ON_EQUITY,
      Fundaments.PAYOUT,
      selfie.isPremium ? Fundaments.REPURCHASE_OF_CAPITAL_STOCK : null,
      selfie.isPremium ? Fundaments.DIVIDEND_YIELD : null,
      selfie.isPremium ? Fundaments.EARNINGS_PER_SHARE : null,
    ].filter(notUndefinedOrNull);
  }

  if (classification.isCompany) {
    return [
      Fundaments.TOTAL_EQUITY,
      Fundaments.REVENUE,
      Fundaments.EBITDA,
      Fundaments.NET_INCOME,
      Fundaments.CASH,
      Fundaments.DEBT,
      Fundaments.NET_DEBT,
      Fundaments.CASH_FLOW_FROM_OPERATIONS,
      Fundaments.FREE_CASH_FLOW,
      Fundaments.CASH_FLOW_FOR_DIVIDENDS,
      Fundaments.NET_MARGIN,
      Fundaments.RETURN_ON_EQUITY,
      Fundaments.PAYOUT,
      selfie.isPremium ? Fundaments.REPURCHASE_OF_CAPITAL_STOCK : null,
      selfie.isPremium ? Fundaments.DIVIDEND_YIELD : null,
      selfie.isPremium ? Fundaments.EARNINGS_PER_SHARE : null,
    ].filter(notUndefinedOrNull);
  }

  if (classification.isInsurer) {
    const hasDebt = financials.some((financialStatementRow) => {
      return financialStatementRow.balanceSheet.debt > 0;
    });

    const hasEbitda = financials.some((financialStatementRow) => {
      return financialStatementRow.incomeStatement.ebitda !== 0;
    });

    return [
      Fundaments.TOTAL_EQUITY,
      Fundaments.REVENUE,
      hasEbitda ? Fundaments.EBITDA : null,
      Fundaments.NET_INCOME,
      Fundaments.CASH,
      hasDebt ? Fundaments.DEBT : null,
      hasDebt ? Fundaments.NET_DEBT : null,
      Fundaments.CASH_FLOW_FROM_OPERATIONS,
      Fundaments.FREE_CASH_FLOW,
      Fundaments.CASH_FLOW_FOR_DIVIDENDS,
      Fundaments.NET_MARGIN,
      Fundaments.RETURN_ON_EQUITY,
      Fundaments.PAYOUT,
      selfie.isPremium ? Fundaments.REPURCHASE_OF_CAPITAL_STOCK : null,
      selfie.isPremium ? Fundaments.DIVIDEND_YIELD : null,
      selfie.isPremium ? Fundaments.EARNINGS_PER_SHARE : null,
    ].filter(notUndefinedOrNull);
  }

  if (classification.isReit) {
    return [
      Fundaments.TOTAL_EQUITY,
      Fundaments.REVENUE,
      Fundaments.EBITDA,
      Fundaments.NET_INCOME,
      Fundaments.CASH,
      Fundaments.DEBT,
      Fundaments.NET_DEBT,
      Fundaments.FUNDS_FROM_OPERATIONS,
      Fundaments.FREE_CASH_FLOW,
      Fundaments.CASH_FLOW_FOR_DIVIDENDS,
      Fundaments.NET_MARGIN,
      Fundaments.RETURN_ON_EQUITY,
      Fundaments.PAYOUT,
      selfie.isPremium ? Fundaments.DIVIDEND_YIELD : null,
      selfie.isPremium ? Fundaments.FFO_PER_SHARE : null,
    ].filter(notUndefinedOrNull);
  }

  return [];
}
